/**
 * 金额工具 —— 全系统唯一的金额计算入口。
 *
 * 铁律（对应总指令第五部分第 1 条）：
 *   钱一律用「整数分」表示和计算，任何地方都不允许出现浮点数的加减乘。
 *   0.1 + 0.2 !== 0.3 这件事在钱上是事故，不是小毛病。
 *
 * 所以：内核只做整数运算；只有在「给人看」的时候才转成 12.50 这种字符串。
 */

/** 金额类型别名：整数分。12.34 元 = 1234 */
export type Cents = number;

/** 单笔订单金额上限（100 万元 = 100000000 分）。防住手滑多打几个 0 的情况。 */
export const MAX_CENTS = 100_000_000;

/** 校验一个值是不是合法的「整数分」 */
export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

/**
 * 断言必须是整数分，否则抛错。
 * @param value 待校验值
 * @param label 出错时显示的中文名字，方便定位是哪一步算错了
 */
export function assertCents(value: unknown, label = '金额'): Cents {
  if (!isCents(value)) {
    throw new Error(`${label}必须是整数分（当前值：${String(value)}）`);
  }
  return value;
}

/** 断言必须是非负整数分 */
export function assertNonNegativeCents(value: unknown, label = '金额'): Cents {
  const cents = assertCents(value, label);
  if (cents < 0) {
    throw new Error(`${label}不能为负数（当前值：${cents}）`);
  }
  if (cents > MAX_CENTS) {
    throw new Error(`${label}超出单笔上限（当前值：${cents}，上限：${MAX_CENTS}）`);
  }
  return cents;
}

/** 断言必须是不小于 1 的正整数（用于数量、份数） */
export function assertPositiveInt(value: unknown, label = '数量'): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${label}必须是大于 0 的整数（当前值：${String(value)}）`);
  }
  if (value > 9999) {
    throw new Error(`${label}过大（当前值：${value}，上限：9999）`);
  }
  return value;
}

/** 整数分相加。空数组返回 0。 */
export function sumCents(values: readonly Cents[]): Cents {
  let total = 0;
  for (const v of values) {
    total += assertCents(v);
  }
  if (!Number.isSafeInteger(total)) {
    throw new Error('金额合计溢出安全整数范围');
  }
  return total;
}

/** 单价 × 数量。两边都必须是整数，结果必然是整数。 */
export function mulCents(unitCents: Cents, quantity: number): Cents {
  assertNonNegativeCents(unitCents, '单价');
  assertPositiveInt(quantity, '数量');
  const total = unitCents * quantity;
  if (!Number.isSafeInteger(total)) {
    throw new Error('金额乘法溢出安全整数范围');
  }
  return total;
}

/**
 * 把用户输入的「元」转成整数分。
 *
 * 关键点：不走 parseFloat。parseFloat('0.29') * 100 === 28.999999999999996，
 * 直接取整会变成 28 分，凭空少收 1 分钱。这里改成按字符串拆整数部分和小数部分。
 *
 * @example parseYuanToCents('12.34') === 1234
 * @example parseYuanToCents('12.3')  === 1230
 * @example parseYuanToCents('12')    === 1200
 */
export function parseYuanToCents(input: string | number): Cents {
  if (typeof input === 'number' && !Number.isFinite(input)) {
    throw new Error(`金额格式不合法：${String(input)}`);
  }
  // 数字先转成字符串，避免科学计数法以外的精度问题
  const raw = typeof input === 'number' ? input.toFixed(2) : String(input).trim();
  const matched = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!matched) {
    throw new Error(`金额格式不合法（只支持最多两位小数，如 12.34）：${String(input)}`);
  }
  const negative = matched[1] === '-';
  const yuanPart = Number(matched[2]);
  const fenPart = Number((matched[3] ?? '').padEnd(2, '0') || '0');
  const cents = yuanPart * 100 + fenPart;
  if (!Number.isSafeInteger(cents)) {
    throw new Error(`金额超出安全范围：${String(input)}`);
  }
  return negative ? -cents : cents;
}

/**
 * 整数分转「元」字符串，固定两位小数，用于展示和小票。
 * @example formatYuan(1234) === '12.34'
 * @example formatYuan(-50)  === '-0.50'
 */
export function formatYuan(cents: Cents): string {
  assertCents(cents, '金额');
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const yuan = Math.floor(abs / 100);
  const fen = abs % 100;
  const text = `${yuan}.${String(fen).padStart(2, '0')}`;
  return negative ? `-${text}` : text;
}

/** 带 ¥ 符号的展示形式 */
export function formatCny(cents: Cents): string {
  return `¥${formatYuan(cents)}`;
}

/**
 * 按比例拆分金额（用于按比例退款、按菜品分摊优惠）。
 * 使用「最大余额法」分配，保证拆出来的每一份加起来必然等于原金额，
 * 不会出现 100 分拆 3 份变成 33+33+33=99 分、凭空少 1 分的情况。
 *
 * @param totalCents 待拆分的总额（整数分，可为正）
 * @param weights 各项权重（非负整数，例如各菜品小计）
 * @returns 与 weights 等长的整数分数组，且合计 === totalCents
 */
export function allocateCents(totalCents: Cents, weights: readonly number[]): Cents[] {
  assertCents(totalCents, '待拆分金额');
  if (weights.length === 0) {
    if (totalCents !== 0) {
      throw new Error('权重为空时待拆分金额必须为 0');
    }
    return [];
  }
  for (const w of weights) {
    if (typeof w !== 'number' || !Number.isSafeInteger(w) || w < 0) {
      throw new Error(`权重必须是非负整数（当前值：${String(w)}）`);
    }
  }
  const weightTotal = weights.reduce((a, b) => a + b, 0);

  // 权重全为 0：无法按比例分，退化为把金额全部记在第一项
  if (weightTotal === 0) {
    return weights.map((_, i) => (i === 0 ? totalCents : 0));
  }

  const sign = totalCents < 0 ? -1 : 1;
  const absTotal = Math.abs(totalCents);

  const exact = weights.map((w) => (absTotal * w) / weightTotal);
  const floored = exact.map((v) => Math.floor(v));
  let remainder = absTotal - floored.reduce((a, b) => a + b, 0);

  // 余数按小数部分从大到小分配
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  const result = floored.slice();
  for (const { i } of order) {
    if (remainder <= 0) break;
    result[i] += 1;
    remainder -= 1;
  }

  return result.map((v) => v * sign);
}
